import { useCallback, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { ApiConstant, AppConstant, KeyConstant, LangConstant } from "const";
import { ApiUtils, HumpUtils, StorageUtils, SystemUtils } from "utils";

const DEFAULT_FILTERS = {
  search: "",
  page: 1,
};

export default function useChannelList() {
  const { t: getLabel } = useTranslation();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const channelListQuery = useQuery({
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: [ApiConstant.REACT_QUERY_KEYS.getChannelList, filters],
    queryFn: async () => {
      const branchId = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_ID);

      const filterCondition = { group_type: 2 };
      if (filters.search) filterCondition.name = filters.search;

      const params = HumpUtils.decamelizeKeys({
        branchId,
        paging: 1,
        limit: AppConstant.PAGE_SIZE,
        offset: (filters.page - 1) * AppConstant.PAGE_SIZE,
        asc: 1,
        groupType: 2,
        filter: JSON.stringify(filterCondition),
      });

      const response = await ApiUtils.createApiWithToken(false).get(ApiConstant.GET_CHANNEL_LIST, params);

      if (response.data?.status === ApiConstant.STT_OK) {
        return HumpUtils.camelizeKeys(response.data);
      }

      throw response.data;
    },
    onError: error => {
      console.error(error);
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_UPDATE_ERROR), "error");
    },
  });

  const responseData = channelListQuery.data?.data || {};
  const channels = (responseData.data || []).map(item => ({ ...item, groupName: JSON.parse(item.name || "{}").name }));
  const totalChannels = responseData.totalElements || 0;
  const totalPages = responseData.totalPages;

  const handleSearchChange = useCallback(
    debounce(value => {
      setFilters(prev => ({ ...prev, search: value, page: 1 }));
    }, AppConstant.INPUT_DEBOUNCE_TIME),
    [],
  );

  const handlePageChange = (_, newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  return {
    channels,
    totalChannels,
    page: filters.page,
    totalPages,
    isLoading: channelListQuery.isLoading,
    isFetching: channelListQuery.isFetching,
    refetch: channelListQuery.refetch,
    channelType: filters.channelType,
    search: filters.search,
    handleSearchChange,
    handlePageChange,
  };
}
